import styled, { css, keyframes } from "styled-components";
import { Link } from "gatsby-link";
import { Fade as Hamburger } from "hamburger-react";
import { media } from "../Theme/default-theme";
import Icon from "../Icon";
import Image from "components/Image";
import LearnMoreBtn from "components/LearnMoreBtn";

const fadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;

const fadeInAnimation = css`
  animation: ${fadeIn} 250ms ease-out;
  animation-fill-mode: forwards;
  animation-iteration-count: 1;
`;

export const ModalSectionBreak = styled.div`
  width: 100%;
  height: auto;
  padding: 0.5px 0;
  background: #696057;
  margin: 3rem 0;
`;

export const ModalSectionButton = styled(LearnMoreBtn)`
  margin: 0 auto;
  padding: clamp(0.5rem, 0.3677rem + 0.6613vw, 1.0625rem)
    clamp(2.0625rem, 1.1808rem + 4.4085vw, 5.8125rem);
  background: transparent;
`;

export const ModalSectionDescription = styled.p`
  display: none;
  font-size: clamp(0.875rem, 0.8456rem + 0.147vw, 1rem);
  line-height: 150%;
  font-weight: 325;
  font-family: Montserrat, sans-serif;
  color: #382e2b;
`;

export const ModalSectionTitle = styled.h4`
  text-transform: uppercase;
  font-family: Montserrat, sans-serif;
  font-size: clamp(1.375rem, 1.2868rem + 0.4409vw, 1.75rem);
  font-weight: 325;
  line-height: 125%;
  color: #382e2b;
`;

export const ModalSectionIconContainer = styled.div`
  width: 6em;
  height: 6em;
  border: 0;
  border-radius: 3em;
  background: #f8f8f8;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: auto;
`;

export const CloseModalIcon = styled(Icon)`
  max-width: fit-content;
  height: auto;
  cursor: pointer;
`;

export const CloseModalButton = styled.button`
  height: 3em;
  width: fit-content;
  border-radius: 1.5em;
  border: 0;
  background: transparent;
  position: absolute;
  left: 0;
  top: 0;
  padding: 2.2rem;
`;

export const SigninModal = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: no-wrap;
  overflow: auto;
  align-content: flex-start;
  transition: 0.5s transform;
  column-gap: 2rem;
  position: fixed;
  left: 0;
  top: 60px;
  width: 100%;
  height: 100vh;
  font-weight: 300;
  background: #f2f2f2;
  justify-content: space-around;
  transform: ${({ isOpen }) =>
    isOpen ? `translateY(0)` : `translateY(calc(-100% - 60px))`};

  padding: 1rem 0 125px;

  ${media.tablet} {
    justify-content: space-around;
    ${ModalSection} {
      margin: 0 12rem;
      width: auto;
      ${ModalSectionDescription} {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        margin: 0 0 2rem;
      }
    }
    ${ModalSectionBreak} {
      margin: 3rem 10rem;
      width: auto;
    }
  }

  @media screen and (orientation: landscape) {
    flex-direction: row;
    position: absolute;
    top: 25vh;
    left: 0;
    right: 0;
    margin: auto;
    width: 65vw;
    height: 61vh;
    padding: 0 5.5rem;
    opacity: 0;
    transform: unset;
    ${({ isOpen }) => isOpen && fadeInAnimation}
    ${ModalSection} {
      width: 45%;
      margin: auto;
    }
    ${ModalSectionBreak} {
      margin: auto;
      height: 70%;
      width: 1px;
    }
  }
`;

export const SigninButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: center;
  border: 0;
  background: transparent;
`;

const navTextSize = css`
  font-size: 1rem;
  font-family: Montserrat, sans-serif;
  line-height: 1.5em;
  font-style: normal;
`;

export const PrimaryExternalLink = styled.a`
  text-decoration: none;
  ${navTextSize};
  color: #382e2b;
  font-weight: 450;
  padding: 0;
  text-transform: uppercase;

  :hover {
    color: #ff6000;
  }
`;

export const SectionTitle = styled.div`
  ${navTextSize};
  cursor: initial;
  font-weight: 450;
  text-transform: uppercase;
  color: #382e2b;
`;

export const ExternalLink = styled.a`
  text-decoration: none;
  ${navTextSize};
  color: #382e2b;
  font-weight: 300;
  padding: 0.25rem 0;

  :hover {
    color: #ff6000;
  }
`;

export const CategoryHeader = styled.h5`
  font-family: Montserrat;
  font-style: normal;
  font-weight: 450;
  font-size: 0.65em;
  line-height: 0.75em;
  text-transform: uppercase;
  color: #696057;
  cursor: default;
  margin: 1.8em 0 0.8em;
`;

export const NavItem = styled.div`
  color: #382e2b;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  margin: 0;
`;

export const NavWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 0 1.125rem;
`;

export const CloseMenuArea = styled.div`
  transition: 1s background;
  position: fixed;
  left: 0;
  top: 61px;
  width: 100%;
  height: calc(100% - 60px);
  background: rgba(0, 0, 0, 0.4);
  display: ${(p) => (p.isOpen ? "block" : "none")};
`;

export const StyledIcon = styled(Icon)`
  display: inline-block;
  cursor: pointer;
  color: #382e2b;
  width: ${(p) => p?.width || "inherit"};
  height: ${(p) => p?.height || "inherit"};
`;

export const Navbar = styled.nav`
  display: flex;
  flex-direction: column;
  flex-wrap: no-wrap;
  overflow: scroll;
  align-content: flex-start;
  transition: 0.5s transform;
  column-gap: 2rem;
  position: fixed;
  left: 0;
  top: 60px;
  width: 100%;
  font-weight: 300;
  background: #f2f2f2;
  justify-content: flex-start;
  height: calc(100% - 60px);
  transform: ${(p) =>
    p.isOpen ? `translateY(0)` : `translateY(calc(-100% - 60px))`};
  padding: 3rem 1.75rem;

  ${media.tablet} {
    overflow-y: unset;
    min-height: 25.5rem;
    height: ${({ total_items }) => total_items}rem;
    margin: auto 0;
    padding: 1rem 2rem 0;
    flex-wrap: wrap;

    ${NavWrapper} {
      width: 13rem;
    }
  }
`;

export const UnorderedList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

export const InternalLink = styled(Link)`
  text-decoration: none;
  ${navTextSize};
  font-weight: 300;
  color: #382e2b;

  :hover {
    color: #03ad95;
  }
`;

export const AppBarText = styled.div`
  ${navTextSize};
  cursor: initial;
  font-weight: 450;
`;

export const ListItem = styled.li`
  ${InternalLink}, ${ExternalLink} {
    font-weight: 300;
  }

  line-height: 1.22em;
`;

export const MenuIconButton = styled(Hamburger)``;

export const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 100%;
  column-gap: 1rem;

  > i {
    margin-left: 25px;
  }
`;

export const IconLink = styled(Icon)`
  display: inline-block;
  cursor: pointer;
  color: #382e2b;
  width: ${(p) => p?.width || "inherit"};
  height: ${(p) => p?.height || "inherit"};
`;

export const AppBarLogo = styled(Image)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
  max-height: 23px;
  width: auto;
`;

export const AppBarLogoLink = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: auto;
`;

export const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 8px;
  position: absolute;
  z-index: 9998;
  width: 100%;
  background: #f2f2f2;
`;

export const SocialLinkItem = styled.div`
  > a {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

export const SocialLinkLabel = styled.div`
  font-size: 8px;
  line-height: 11px;
  margin-top: 4px;
  color: #382e2b;
`;

export const SocialLinksWrapper = styled.div`
  width: 15.5rem;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  column-gap: 1rem;

  svg {
    width: 25px;
    height: 25px;
  }
`;

export const Container = styled.div`
  height: 60px;
  background: #000;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 9999;

  ${media.tablet} {
    ${Content} {
      padding: 0 32px 0 18px;
      ${IconLink} {
        ${AppBarLogo} {
          width: 25rem;
        }
      }
    }

    ${SocialLinksWrapper} {
      margin-top: 0;
    }
  }

  ${media.desktop} {
  }
`;

export const SearchContainer = styled.div`
  position: relative;
  height: 4rem;
  background-color: #e6e6e6;
  top: 3.5rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const SearchUl = styled.ul`
  position: absolute;
  background: #fff;
  max-height: 500px;
  overflow: auto;
  top: 100%;
  margin: 0;
  width: 343px;
  padding: 0;
  list-style-type: none;
  filter: drop-shadow(0 4px 4px rgba(0, 0, 0, 0.25));
`;

export const SearchLi = styled.li`
  padding: 20px;
  display: flex;
  align-items: center;

  > i {
    width: 17px;
    height: 17px;
  }

  > a {
    margin-left: 8px;
    text-decoration: none;

    &:hover {
      color: #ff6000;
    }
  }
`;

export const SearchInput = styled.input`
  border: none;
  width: 262px;
  border-bottom: 1px solid #827c7b;
  background: #f2f2f2;
  outline: none;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  color: #382e2b;
`;

export const SearchIconWrapper = styled.div``;
